import { useEffect, useState } from 'react';
import { IS_MOBILE } from '../config';
import { assetUrl } from './assets';

// carFleet — the traffic car models, read from /models/cars/cars.json.
//
// A MANIFEST rather than a directory listing, because the browser cannot list a
// folder: dropping a .glb in is half the job, the other half is one line of
// JSON. That line is also where a per-car override lives on the rare model the
// auto-fit gets wrong.
//
// Loaded ONCE for the whole world and shared. Every car on every route pulls
// from the same list, so a fleet of twelve cars is one fetch, not twelve.
//
// TWO RULES ABOUT *WHEN*, both learned the hard way on the intro:
//
//   1. NOT DURING THE FLY-IN. Cars are ambient life, not part of the establishing
//      shot, and the intro is the one moment the frame budget is already spent —
//      city and skyline decoding, billboards starting video, residents streaming.
//      Adding four GLB parses and ~55 texture uploads on top of that stalled even
//      a strong machine. The fleet waits for the camera to settle; until then the
//      routes draw their blocks, which at fly-in distance are indistinguishable.
//
//   2. ONE AT A TIME. Revealing the whole manifest at once mounts every model in
//      the same frame, which is the same stall moved later rather than removed.
//      Each car is revealed only after the previous one reports itself loaded, so
//      the parses and GPU uploads land in separate frames. Traffic starts as all
//      one model and gains variety over the next second or two — nobody watching
//      the street notices the fleet filling in, and everybody notices a freeze.
// The manifest and the cars it names both resolve through assetUrl, so the
// fleet can live in public/ today and in the bucket tomorrow. A `file` entry
// starting with "/" is local; anything else is a bucket object name.
const MANIFEST = '/models/cars/cars.json';

let all = null;          // every car in the manifest
let revealed = 0;        // how many are allowed to mount yet
let loading = null;
let timer = null;
let firstTimer = null;
const subs = new Set();
const reported = new Set();

const notify = () => subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });

// A model that never resolves — a bad GLB, a 404 — must not strand the rest of
// the fleet behind it. The failsafe advances anyway, so a broken car costs its
// own slot and nothing else.
function arm() {
  clearTimeout(timer);
  if (!all || revealed >= all.length) return;
  timer = setTimeout(() => {
    console.warn(`[cars] ${all[revealed - 1]?.url} did not report ready — revealing the next anyway`);
    advance();
  }, 5000);
}

function advance() {
  clearTimeout(timer);
  if (!all || revealed >= all.length) return;
  revealed++;
  notify();
  arm();
}

// Called by CarModel once its GLTF has parsed. Deferred a beat so the first
// frame that UPLOADS the model's textures to the GPU finishes before the next
// model starts parsing — the upload, not the parse, is the expensive half.
function noteReady(url) {
  if (reported.has(url)) return;
  reported.add(url);
  setTimeout(advance, 300);
}

// A PHONE GETS A SMALLER FLEET, NOT A WORSE ONE.
//
// Variety is the point of a fleet, but variety is also the whole cost: four
// models is four downloads, four parses, four sets of textures resident, and on
// this manifest roughly 250 draw calls where the blocks cost 36. A phone cannot
// pay that, and it was paying it — the fleet shipped with no mobile gate at all.
//
// So mobile keeps the CHEAPEST models rather than dropping traffic to blocks:
// real cars still drive, there are simply fewer kinds. Chosen by declared draw
// count so the choice keeps making sense as cars are added, rather than naming a
// file here that someone later replaces.
//
// TWO, not one. One model is measurably cheapest and reads as a bug — a street
// where every single car is the same car is something you notice immediately,
// and "the fleet is broken" is a worse outcome than a few more draw calls. Two
// costs roughly 65 draws across the thinned mobile traffic against 248 before,
// so the saving is still about 4x and the street stops looking cloned.
const MOBILE_FLEET_MAX = 2;

async function load() {
  const d = await fetch(assetUrl(MANIFEST), { cache: 'no-store' })
    .then((r) => r.json())
    .catch(() => null);
  const fallbackLength = d?.length ?? 4;
  let entries = (d?.cars || []).filter((c) => c && c.file);
  if (IS_MOBILE && entries.length > MOBILE_FLEET_MAX) {
    const cost = (c) => c.draws ?? c._draws ?? 999;
    entries = [...entries].sort((a, b) => cost(a) - cost(b)).slice(0, MOBILE_FLEET_MAX);
    console.log(`[cars] mobile — fleet trimmed to ${entries.map((c) => c.name || c.file).join(', ')}`);
  }
  all = entries
    .map((c, i) => {
      const url = assetUrl(c.file.startsWith('/') ? c.file : `/models/cars/${c.file}`);
      // The ready-signal key is PER ENTRY, not per url: painted variants (the
      // Elise fleet) are five entries sharing one file, and a url-keyed dedupe
      // would swallow every report after the first — stalling the reveal on
      // the 5s failsafe four times in a row.
      const key = `${url}#${i}`;
      return {
        url,
        length: c.length ?? fallbackLength,
        // null means "measure it" — see CarModel.
        scale: c.scale ?? null,
        rotY: c.rotY ?? null,
        // Body repaint (CarModel clones painted materials). One model, many
        // colours — variety at zero extra downloads, parses, or draw calls.
        tint: c.tint ?? null,
        // Bound once, not per render, so CarModel's effect deps stay stable.
        onReady: () => noteReady(key),
      };
    });
  if (all.length) console.log(`[cars] manifest ready — ${all.length} model(s), revealing one at a time`);
  else console.log('[cars] no models in /models/cars/cars.json — traffic falls back to blocks');
  warmCache();
  beginReveal();
  notify();
  return all;
}

// DOWNLOAD EARLY, DECODE LATE — the two halves of "loading" have opposite needs.
//
// Everything here is gated on the camera settling, which is right for DECODING:
// parsing a GLB and uploading it is main-thread work, and doing four at once as
// the fly-in lands is the stampede the staged reveal exists to prevent.
//
// It was wrong for DOWNLOADING. The fetch is off-thread and costs the frame
// nothing, but gating it too meant not a single byte moved until the camera had
// already arrived — so the fly-in, several seconds of otherwise idle network,
// was spent doing nothing, and the wait was moved to precisely the moment the
// player is standing still with nothing to look at.
//
// A plain fetch warms the HTTP cache without decoding anything, so the staged
// reveal afterwards parses from memory instead of the network. The body is READ,
// not just requested: an unconsumed response is not reliably written to the
// cache, which would make the whole exercise a no-op. Same trick CityModel and
// CitySkyline already use at module scope.
let warmed = false;
function warmCache() {
  if (warmed || !all?.length) return;
  warmed = true;
  // De-duped: painted variants share a file, and warming the same URL five
  // times is four wasted requests.
  const urls = [...new Set(all.map((c) => c.url))];
  for (const url of urls) {
    fetch(url).then((r) => r.arrayBuffer()).catch(() => { /* the real load reports failures */ });
  }
  console.log(`[cars] pre-fetching ${urls.length} file(s) for ${all.length} fleet entr(ies) during the fly-in — decode still waits for the reveal`);
}

// Let the settle frame itself pass before the first model lands. The billboards
// are staggering their videos over the same seconds, and the camera arriving is
// the one moment a stall is most obvious — the street can gain its first real car
// a beat later without anyone noticing. Guarded and idempotent because there are
// two ways in: the manifest finishing, and the gate opening on an already-loaded
// manifest.
const FIRST_CAR_DELAY_MS = 1200;
function beginReveal() {
  if (!all || !all.length || revealed !== 0 || firstTimer) return;
  firstTimer = setTimeout(() => {
    firstTimer = null;
    if (revealed !== 0) return;
    revealed = 1;
    notify();
    arm();
  }, FIRST_CAR_DELAY_MS);
}

// Which car starts the rotation. Per LOAD, so the same model is not always on
// the same corner, and stable within a session so cars do not swap under you.
export const FLEET_SHIFT = Math.floor(Math.random() * 997);

// `enabled` is the gate — pass the camera-settled flag.
//
// Returns the WHOLE manifest plus how many are revealed, rather than just the
// revealed slice, so a caller can give each car slot a FIXED model up front and
// show a block until that particular model arrives. Handing back a growing list
// instead would make `slot % fleet.length` land somewhere new every time the
// list grew, and cars already on screen would visibly morph into other cars as
// the fleet filled in. Each slot resolves once, from block to its own car.
export function useCarFleet(enabled = true) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const fn = () => tick((n) => n + 1);
    subs.add(fn);
    if (!all && !loading) loading = load();
    else if (all) beginReveal();
    return () => subs.delete(fn);
  }, [enabled]);
  if (!enabled || !all) return { cars: [], revealed: 0 };
  return { cars: all, revealed };
}
